// bluaIQ — Payment Warning Banner
// Shows after 1st failed payment attempt (yellow warning)
// 7-day grace period before account is blocked
// "Update payment" opens Stripe Customer Portal
//
// daysLeft is optional. When omitted (no real timing data from backend yet)
// the banner shows an honest no-count message. When a real count is available
// (future: paymentFailedAt → graceEndsAt in /api/subscription) the
// count-specific message is used automatically.

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { AlertTriangle, CreditCard, X } from 'lucide-react'

interface PaymentWarningBannerProps {
  /** Days remaining in grace period (7 max). Omit when real backend timing
   *  is not yet available — the banner falls back to an honest no-count
   *  message rather than displaying a potentially incorrect number. */
  daysLeft?: number | null
  onUpdatePayment: () => void
  onDismiss: () => void
}

export default function PaymentWarningBanner({ daysLeft, onUpdatePayment, onDismiss }: PaymentWarningBannerProps) {
  const { i18n } = useTranslation()
  const { getToken } = useAuth()
  const isEs = i18n.language === 'es'
  const [loading, setLoading] = useState(false)

  const handleUpdatePayment = async () => {
    setLoading(true)
    try {
      const token = await getToken()
      const res = await apiFetch('/api/stripe/portal', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
        return
      }
    } catch {
      // Stripe portal not available — fall back to original handler
    }
    setLoading(false)
    onUpdatePayment()
  }

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 flex items-center gap-3 relative z-[60]">
      <AlertTriangle size={16} className="flex-shrink-0 animate-pulse" />
      <div className="flex-1 min-w-0">
        <span className="text-[12px] font-[800]">
          {isEs
            ? (daysLeft != null
                ? `Tu pago fue rechazado. Tienes ${daysLeft} días para actualizar tu método de pago.`
                : `Tu pago falló. Actualiza tu método de pago para mantener acceso completo.`)
            : (daysLeft != null
                ? `Your payment failed. You have ${daysLeft} days to update your payment method.`
                : `Your payment failed. Please update your payment method to keep full access.`)
          }
        </span>
      </div>
      <button
        onClick={handleUpdatePayment}
        disabled={loading}
        className="flex items-center gap-1.5 bg-white text-amber-600 text-[13px] font-[800] px-4 py-1.5 rounded-full hover:bg-amber-50 transition-colors flex-shrink-0"
      >
        <CreditCard size={12} />
        {loading ? '...' : (isEs ? 'Actualizar pago' : 'Update payment')}
      </button>
      <button onClick={onDismiss} className="p-0.5 hover:bg-white/20 rounded flex-shrink-0">
        <X size={14} />
      </button>
    </div>
  )
}
